import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';
import { signupHrefForPath } from '@/lib/presetFromPath';

import { AUDIOS_IN_PLAN } from '@/constants/common';
import { TRANSCRIPTION_LANGUAGES } from '@/constants/transcription';
import { useUser } from '@/utils/useUser';

/**
 * The transcription landing pages' `t`. A key a locale hasn't translated yet
 * falls back to the English key with its {{placeholders}} filled in:
 * next-translate otherwise returns a missing key raw, placeholders and all.
 */
export function useLandingT() {
  const { t, lang } = useLocale('ai-transcription');
  return {
    lang,
    t: (key: string, query?: Record<string, unknown>) =>
      // English is the key itself: skip the lookup (no English file, and no
      // missing-key warning per string in development).
      lang === 'en' ? interpolate(key, query) : t(key, query, { default: key }),
  };
}

const interpolate = (text: string, query?: Record<string, unknown>) =>
  query
    ? text.replace(/{{\s*(\w+)\s*}}/g, (match, name: string) =>
        name in query ? String(query[name]) : match,
      )
    : text;

export type LandingT = ReturnType<typeof useLandingT>['t'];

/**
 * Values for a page's {{placeholders}}: its own vars, translated (a language
 * page's language name), plus {{languages}} (how many languages the app
 * offers) and {{free}} / {{starter}} (monthly transcriptions on those plans).
 * Each var also comes as {{<name>_lower}}, first letter lowercased.
 */
export function usePageVars(page: { vars?: Record<string, string> }) {
  const { t, lang } = useLandingT();
  const vars: Record<string, unknown> = {
    languages: TRANSCRIPTION_LANGUAGES.length,
    free: AUDIOS_IN_PLAN.Free,
    starter: AUDIOS_IN_PLAN.Starter,
  };
  for (const [key, value] of Object.entries(page.vars ?? {})) {
    const text = t(value);
    vars[key] = text;
    // For mid-sentence use where language names are lowercase (French
    // "en japonais", Vietnamese "tiếng Nhật").
    vars[`${key}_lower`] =
      text.charAt(0).toLocaleLowerCase(lang) + text.slice(1);
  }
  return vars;
}

/**
 * Where a page's call to action leads: the app for signed-in visitors, the
 * sign-up form (which hands over to the same tool afterwards) for others.
 */
export function useStartHref(cta: 'upload' | 'record' = 'upload') {
  const router = useRouter();
  const { user } = useUser();
  if (user) return cta === 'record' ? '/note-taker' : '/audios';
  return signupHrefForPath(cta === 'record' ? '/ai-note-taker' : router.asPath);
}

/** Security and privacy standards Easy-Peasy.AI meets (see the Trust Center). */
export const COMPLIANCE = ['SOC 2', 'ISO 27001', 'GDPR', 'HIPAA'];

/** Formats people search for most; the app takes about 40 audio and video types. */
export const FILE_FORMATS = [
  'MP3',
  'WAV',
  'M4A',
  'MP4',
  'MOV',
  'AAC',
  'FLAC',
  'OGG',
  'OPUS',
  'WEBM',
  'WMA',
  'AMR',
  'AIFF',
  'M4V',
  'MKV',
  'MTS',
];

/** Small round glyph for the speaker in a transcript line. */
export function SpeakerDot({
  color,
  className,
}: {
  color: string;
  className?: string;
}) {
  return (
    <span
      aria-hidden='true'
      className={clsxm('h-2 w-2 flex-none rounded-full', color, className)}
    />
  );
}

export function ShieldGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 20 20'
      fill='currentColor'
      aria-hidden='true'
      className={clsxm('h-4 w-4 flex-none', className)}
    >
      <path
        fillRule='evenodd'
        d='M9.661 2.237a.531.531 0 01.678 0 11.947 11.947 0 007.078 2.749.5.5 0 01.479.425c.069.52.104 1.05.104 1.59 0 5.162-3.26 9.563-7.834 11.256a.48.48 0 01-.332 0C5.26 16.564 2 12.163 2 7c0-.538.035-1.069.104-1.589a.5.5 0 01.48-.425 11.947 11.947 0 007.077-2.75zm4.196 5.954a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z'
        clipRule='evenodd'
      />
    </svg>
  );
}

export function PlayGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 20 20'
      fill='currentColor'
      aria-hidden='true'
      className={clsxm('h-4 w-4 flex-none', className)}
    >
      <path d='M6.3 2.84A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.27l9.344-5.891a1.5 1.5 0 000-2.538L6.3 2.841z' />
    </svg>
  );
}
